import React from 'react';
import { Icon } from 'react-materialize';
import { CONTACT_EMAIL, EMAIL_SUBJECT, ZOOM_SCHEDULING_LINK } from '../../config/constants';
import meditationhall from '../../img/backgrounds/meditationhall.avif';
import photo2 from '../../img/backgrounds/photo-1528183429752-a97d0bf99b5a_wide.avif';
import photo3 from '../../img/backgrounds/photo-1660156932308-9ad748f044dd.avif';
import { BlogLink } from '../blog-link';
import { BrushStrokeAnimation } from '../brush-stroke';
import { Credentials } from '../credentials';
import { HeroPortrait } from '../hero-portrait';
import { Paraquote } from '../paraquote';
import { Testimonials } from '../testimonials';
import './styles.scss';

export const BodyComponent = () => {
  return (
    <div className='Body'>
      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <BrushStrokeAnimation minInterval={3} maxInterval={8} />
        <div className="container">
          <p className="hero-description hero-description-top">
            Mindfulness at the crossroads of clarity and compassion&nbsp;— where&nbsp;resilience, ease, and connection take root.
          </p>
          <div className="hero-content">
            <HeroPortrait />
            <div className="hero-text">
              <p className="one-liner">
                I help people feel more capable and more at ease through simple, secular mindfulness practices—practical tools shaped by nearly two decades of study and rooted in traditions increasingly validated by modern science, supporting <strong>clarity</strong>, <strong>resilience</strong>, and <strong>genuine connection</strong>.
              </p>
              <hr className="hero-divider" />
<p className="one-liner">
              My work centers on <strong>meditation practice</strong> — cultivating steady attention and clear seeing — with mindfulness tools that support and deepen that training.
           </p>
            </div>
          </div>
        </div>
      </section>

      {/* Parallax Quote 1 */}
      <Paraquote
        backgroundImage={photo3}
        quoteText="The present moment is the only time over which we have dominion"
        quoteAuthor="Thich Nhat Hanh"
        height={400}
        textOffset={120}
      />

      {/* About Section */}
      <section id="about" className="section">
        <div className="container">
          <h2>About</h2>

          <div className="positioning-statement">
            <p>
              I teach <strong>mindfulness and meditation</strong> in a clear, relatable way that helps people feel more capable, more at ease in their own minds, and more connected to the shared experience of being human. My approach centers on cultivating steady attention and clear awareness, supported by simple, <strong>secular mindfulness tools</strong>, deep engagement with Tibetan Buddhist philosophy, <strong>long‑term training</strong>, and years of <strong>real‑world teaching experience</strong>.
            </p>

            <div className="about-image-wrapper">
              <img src={meditationhall} alt="Meditation hall" className="about-image" />
            </div>

            <p>
              With a background that spans both contemplative practice and the tech industry — from founding a digital studio serving Fortune 500 clients to working as a staff engineer at Warner Brothers Discovery — I translate timeless meditation practices into <strong>practical, workplace‑friendly guidance</strong>.
            </p>

            <p>
              Whether working with organizations or individuals, I offer accessible tools that strengthen <strong>focus</strong>, <strong>resilience</strong>, <strong>emotional intelligence</strong>, and a steadier sense of presence. Participants leave with strategies they can apply right away, <strong>a more supportive relationship with their own thoughts</strong>, and greater confidence in their ability to meet daily challenges with clarity and composure.
            </p>

            <p>It is important to note that I offer mindfulness instruction and consulting designed to support a more <strong>grounded relationship with everyday experience</strong>. My work is <strong>educational and practice‑based</strong> — not clinical or therapeutic — and is meant to complement, not replace, professional medical or mental‑health care.</p>

            <Credentials />
          </div>
        </div>
      </section>

        {/* Testimonials Section */}
      <Testimonials />


      {/* Services Section */}
      <section id="services" className="section">
        <div className="container">
          <h2>Services</h2>

          <p className="services-intro">
            Whether working with individuals or organizations, I offer <strong>meditation‑based training</strong> that strengthens attention, deepens awareness, and supports a more grounded relationship with experience. Each offering emphasizes <strong>practical skills you can apply right away</strong>, with a focus on <strong>safety</strong>, <strong>agency</strong>, and <strong>personal choice</strong>.
          </p>
          <div className="service-item">
            <h3>Individual Meditation &amp; Mindfulness Coaching</h3>
            <p>One-on-one sessions tailored to your specific needs, helping you develop practical mindfulness tools for daily life. Sessions are available in-person in the Buffalo, NY area or via Zoom.</p>
          </div>
          <div className="service-item">
            <h3>Workplace Meditation &amp; Mindfulness Workshops</h3>
            <p>Workplace-friendly mindfulness training designed to strengthen focus, resilience, and emotional intelligence in professional settings. Corporate workshops are available in-person or via Zoom.</p>
          </div>

           <div className="service-item">
            <h3>Group Sessions</h3>
            <p>Community-based practice sessions and talks that foster connection and shared learning in a supportive environment. These can be tailored to your group's specific needs and can be offered in-person or via Zoom.</p>
            <p>
            Possible topics include:
            <ul>
              <li>Introduction to mindfulness/awareness meditation</li>
              <li>Mindfulness in daily life</li>
              <li>Mindfulness in the workplace</li>
              <li>Working with stress and anxiety</li>
              <li>Finding peace in times of social unrest</li>
            </ul>
            </p>
          </div>

          <div className="service-item">
            <h3>Open Meditation Sessions</h3>
            <p>Open meditation sessions are currently offered via Zoom every Sunday at 10:00am ET. These consist of 25 minutes of sitting meditation, 10 minutes of walking meditation (or break), and 25 minutes of sitting meditation and are often accompanied by a short check-in, dharma talk or discussion.</p>

            <p>
              New participants are encouraged to <a href={ZOOM_SCHEDULING_LINK} target="_blank" rel="noopener noreferrer" className="booking-button">book an introductory consultation</a> and/or have an established meditation practice prior to attending.
            </p>
          </div>


        </div>
      </section>


      {/* Parallax Quote 2 */}
      <Paraquote
        backgroundImage={photo2}
        quoteText="Mindfulness is about being fully awake in our lives"
        quoteAuthor="Jon Kabat-Zinn"
        height={400}
        textOffset={120}
      />

      {/* Resources/Blog Section */}
      <BlogLink />

      {/* Contact Section */}
      <section id="contact" className="section">
        <div className="container">
          <h2>Contact</h2>
          <p>Ready to chat? Reach out with any questions or schedule a no-cost 15 minute introductory consultation.</p>
          <div className="contact-buttons">
            <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(EMAIL_SUBJECT)}`} className="email-button">
              <Icon className="email-icon">send</Icon>
              Email JR
            </a>
            <a href={ZOOM_SCHEDULING_LINK} target="_blank" rel="noopener noreferrer" className="booking-button">
              <Icon className="booking-icon">event</Icon>
              Book a Session
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
